/**
 * «AÑADIR ACTUALIZACIÓN» desde la ficha del foco (22-09-2026): el admin o el
 * encargado que ha ido a mirar (o que sabe cómo está) lo apunta aquí mismo —
 * cómo está, gravedad, foto o audio— y el muestreo queda dentro del foco
 * confirmado en el acto. «Ya no hay» cierra el foco como revisión limpia.
 *
 * Hoja inferior de la casa (`FormSheet`) y sin «Nota» (Pablo, 27-09-2026): en el
 * móvil era un diálogo centrado que no cabía, y la nota nadie la usaba —lo que hay
 * que contar va en el audio—.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Mic, Square, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormSheet, FormSheetPrimaryButton, FormSheetSecondaryButton } from "@/components/ui/FormSheet";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useAudioRecorder, type AudioTake } from "@/hooks/useAudioRecorder";
import { useFocusUpdate } from "@/hooks/useFocusUpdate";
import type { ConfirmSeverity } from "@/hooks/useConfirmScoutingSession";
import type { FocusDetail } from "@/hooks/useFocusTimeline";
import type { FollowUpVerdict } from "@/lib/pests/followUp";
import { VerdictButtons } from "@/components/worker/VerdictButtons";
import { PhotoSourceButtons, PhotoStrip } from "./PhotoSourceButtons";
import { useObjectUrls } from "@/hooks/useObjectUrls";
import { todayIso } from "@/lib/reports/dates";

const SEVERITIES: ConfirmSeverity[] = ["low", "medium", "high", "critical"];
const CHIP = "rounded-xl border px-2 py-2 text-center text-[0.85rem] font-semibold transition-colors";

export function FocusUpdateDialog({ focus, open, onOpenChange }: { focus: FocusDetail | null; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const update = useFocusUpdate(focus);
  const [verdict, setVerdict] = useState<FollowUpVerdict | null>(null);
  const [severity, setSeverity] = useState<ConfirmSeverity | null>(null);
  // El día LOCAL: con `toISOString()` entre las 00:00 y las 02:00 en España salía «ayer».
  const [date, setDate] = useState(todayIso());
  /* E: «ya no hay» cierra el foco; si tenía cosas por hacer, se pregunta si anularlas. */
  const [cancelTasks, setCancelTasks] = useState<boolean | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [audio, setAudio] = useState<AudioTake | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const previews = useObjectUrls(photos);
  const recorder = useAudioRecorder(
    (take) => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudio(take);
      setAudioUrl(URL.createObjectURL(take.blob));
    },
    () => toast({ title: t("worker.incident.micPermissionError"), variant: "destructive" }),
  );

  const reset = () => {
    setVerdict(null); setSeverity(null); setDate(todayIso()); setPhotos([]); setCancelTasks(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudio(null); setAudioUrl(null);
  };
  const close = (o: boolean) => { if (!o) { recorder.stop(); reset(); } onOpenChange(o); };

  const gone = verdict === "gone";
  const liveFollowUps = focus?.timeline.filter((i) => i.kind === "followup_requested" && !i.done && !i.cancelled).length ?? 0;
  const liveTreatments = focus?.timeline.filter((i) => i.kind === "treatment_planned" && i.task.status !== "completing").length ?? 0;
  const askTasks = gone && liveFollowUps + liveTreatments > 0;
  const missing: "verdict" | "severity" | "tasks" | null = !verdict ? "verdict" : !gone && !severity ? "severity" : askTasks && cancelTasks === null ? "tasks" : null;

  const save = async () => {
    if (!focus || !verdict || missing) return;
    const r = await update.submit({ verdict, severity: gone ? null : severity, date, note: "", photos, audio, cancelTasks: askTasks ? cancelTasks === true : undefined });
    if (r.ok) {
      toast({ title: t(r.closed ? "healthMap.focus.done.clean_check" : "healthMap.focus.update.done") });
      close(false);
    } else if ("messageKey" in r) {
      toast({ title: t(r.messageKey), variant: "destructive" });
    }
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={close}
      title={t("healthMap.focus.actions.addUpdate")}
      isDirty={!!verdict || !!severity || photos.length > 0 || !!audio}
      footer={
        <>
          <FormSheetSecondaryButton type="button" onClick={() => close(false)}>{t("common.cancel")}</FormSheetSecondaryButton>
          <FormSheetPrimaryButton type="button" onClick={() => void save()} disabled={!!missing || update.busy || recorder.recording} data-testid="focus-update-save" data-missing={missing ?? ""}>
            {update.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t(gone ? "healthMap.focus.close.ok" : "healthMap.focus.update.save")}
          </FormSheetPrimaryButton>
        </>
      }
    >
        <div className="space-y-4 pb-1" data-testid="focus-update-dialog">
          <div className="space-y-1.5">
            <p className="text-[0.9rem] font-semibold text-slate-900">{t("worker.incident.followup.howNow")}</p>
            <VerdictButtons value={verdict} onPick={setVerdict} />
          </div>
          {!gone && (
            <div className="space-y-1.5" data-testid="focus-update-severity">
              <p className="text-[0.9rem] font-semibold text-slate-900">{t("healthMap.focus.update.severity")}</p>
              <div className="grid grid-cols-4 gap-1.5" role="radiogroup">
                {SEVERITIES.map((s) => (
                  <button key={s} type="button" role="radio" aria-checked={severity === s} onClick={() => setSeverity(s)} data-testid={`focus-update-severity-${s}`} data-selected={severity === s}
                    className={cn(CHIP, severity === s ? "border-slate-800 bg-slate-800 text-white" : "border-[#b6dded] bg-white text-slate-700")}>
                    {t(`worker.incident.severityLevels.${s}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* En un teléfono estrecho, fecha y audio uno debajo del otro: lado a lado el
              reproductor de audio quedaba en un hilo. */}
          <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 min-[400px]:gap-2">
            <div className="space-y-1">
              <p className="text-[0.8rem] font-semibold text-slate-600">{t("healthMap.focus.update.date")}</p>
              <Input type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} className="bg-white" data-testid="focus-update-date" />
            </div>
            <div className="space-y-1">
              <p className="text-[0.8rem] font-semibold text-slate-600">{t("worker.review.audio")}</p>
              {audio && !recorder.recording ? (
                <div className="flex items-center gap-1">
                  {audioUrl && <audio controls src={audioUrl} className="h-9 min-w-0 flex-1" data-testid="focus-update-audio" />}
                  <button type="button" onClick={() => { setAudio(null); if (audioUrl) URL.revokeObjectURL(audioUrl); setAudioUrl(null); }} className="p-1.5 text-red-600" aria-label={t("common.delete")}><Trash2 className="h-4 w-4" /></button>
                </div>
              ) : recorder.recording ? (
                <Button type="button" variant="outline" className="w-full border-red-300 bg-red-50 text-red-700" onClick={recorder.stop} data-testid="focus-update-audio-stop">
                  <Square className="mr-1.5 h-4 w-4" /> {t("worker.review.stopAudio")} · {recorder.seconds}s
                </Button>
              ) : (
                <Button type="button" variant="outline" className="w-full" onClick={() => void recorder.start()} data-testid="focus-update-audio-record">
                  <Mic className="mr-1.5 h-4 w-4" /> {t("worker.review.recordAudio")}
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-1.5" data-testid="focus-update-photos" data-count={photos.length}>
            {/* «Hacer foto» a un lado y «Subir foto» al otro, y las fotos en fila (23-09-2026): ver `PhotoSourceButtons`. */}
            <PhotoSourceButtons testIdPrefix="focus-update" onFiles={(files) => setPhotos((xs) => [...xs, ...files])} />
            <PhotoStrip urls={previews} onRemove={(i) => setPhotos((xs) => xs.filter((_, j) => j !== i))} testId="focus-update-photo-strip" />
          </div>
          {gone && <p className="text-[0.8rem] text-emerald-800">{t("healthMap.focus.close.reason.cleanHint")}</p>}
          {askTasks && (
            <div className="space-y-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid="focus-update-tasks">
              <p className="text-[0.85rem] font-semibold text-amber-900">{t("healthMap.focus.close.tasks.title")}</p>
              <p className="text-[0.8rem] text-amber-800">
                {[
                  liveFollowUps > 0 ? t("healthMap.focus.close.tasks.followups", { n: liveFollowUps }) : null,
                  liveTreatments > 0 ? t("healthMap.focus.close.tasks.treatments", { n: liveTreatments }) : null,
                ].filter(Boolean).join(" · ")}
              </p>
              <div className="grid grid-cols-2 gap-1.5" role="radiogroup" aria-label={t("healthMap.focus.close.tasks.title")}>
                {([true, false] as const).map((v) => (
                  <button key={String(v)} type="button" role="radio" aria-checked={cancelTasks === v} onClick={() => setCancelTasks(v)} data-testid={`focus-update-tasks-${v ? "cancel" : "keep"}`} data-selected={cancelTasks === v}
                    className={cn(CHIP, cancelTasks === v ? "border-slate-800 bg-slate-800 text-white" : "border-[#b6dded] bg-white text-slate-800")}>
                    {t(v ? "healthMap.focus.close.tasks.cancel" : "healthMap.focus.close.tasks.keep")}
                  </button>
                ))}
              </div>
            </div>
          )}
          {missing === "severity" && <p className="text-center text-[0.8rem] font-semibold text-amber-800">{t("healthMap.focus.update.severityMissing")}</p>}
        </div>
    </FormSheet>
  );
}
